import { useAtomValue, useSetAtom } from "jotai";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { SyntheticEvent, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import {
  ZapNodeData,
  ZapMetadata,
  nodesAtom,
  toggleZapStepNodesAndEdgesAtomFamily,
  isViewOnlyModeAtom,
  saveCanvasAsyncCommand,
  isValidUUID,
  togglesAtomResettable,
  isTemplatePageAtom,
} from "@zapier/canvas-state";
import { getItemsForStepSummary } from "../../../../utils/getItemsForStepSummary";
import { TimeSaved } from "../../../TimeMetrics";
import { StepSummary } from "../../../StepSummary";
import { AssetHeading } from "../../../AssetHeading";
import { FlexRow } from "../../../FlexRow";
import StepTitle from "../../../StepTitle/StepTitle";
import { StepStatusRow } from "../../../StepStatusRow";
import { ZapMetrics } from "./ZapMetrics";
import styles from "./ZapNode.module.css";
import { DraftTag } from "../../DraftTag/DraftTag";

interface ZapNodeContentProps {
  data: ZapNodeData;
  /* node id */
  id: string;
  isSelected?: boolean;
  className?: string;
  zap?: ZapMetadata;
  isZapNotFound?: boolean;
  onUpdateNode?: (
    data: Partial<ZapNodeData>,
    options?: { shouldSave: boolean },
  ) => void;
  children?: React.ReactNode;
}

export function ZapNodeContent({
  data,
  id,
  isSelected = false,
  className,
  zap,
  onUpdateNode,
  children,
}: ZapNodeContentProps) {
  const { zapId, isExpanded, label, actors } = data;
  const zapTitle = zap?.title ?? "";
  const hasNote = !isRemirrorJSONEmpty(data.notesJson);
  const steps = zap?.steps || [];
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const hasSetZapTitleAsStepTitle = useRef(false);
  const nodes = useAtomValue(nodesAtom);
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const isTemplatePage = useAtomValue(isTemplatePageAtom);
  const toggleZapStepsAndEdges = useSetAtom(
    toggleZapStepNodesAndEdgesAtomFamily(id),
  );
  const isDraftNode = !isValidUUID(id);
  const toggles = useAtomValue(togglesAtomResettable);
  const shouldShowDraftState =
    isDraftNode && toggles.aiCanvasComposer && !isTemplatePage;
  /** Persist whether Zap node is expanded, so we can show the same UI on page load.
   * React Flow state updates is handled by toggleZapStepsAndEdges */
  const saveZapNodeToBE = useCallback(async () => {
    const updatedNodes = nodes.map((node) => {
      const isZapNode = node.id === id;

      if (!isZapNode) {
        return node;
      }

      return {
        ...node,
        data: {
          ...node.data,
          isExpanded: !isExpanded,
        },
      };
    });

    if (!isViewOnlyMode) {
      saveToBackend({ nodes: updatedNodes });
    }
  }, [id, isExpanded, nodes, saveToBackend, isViewOnlyMode]);

  const toggleButton = useCallback(
    (event: SyntheticEvent<HTMLButtonElement | HTMLUListElement>) => {
      /** To prevent the sidebar from opening */
      event.stopPropagation();
      toggleZapStepsAndEdges(Boolean(isExpanded));
      saveZapNodeToBE();
    },
    [isExpanded, saveZapNodeToBE, toggleZapStepsAndEdges],
  );

  useEffect(() => {
    // If there was no step title, set the Zap title as the step title
    if (
      !hasSetZapTitleAsStepTitle.current &&
      !label &&
      zapTitle &&
      onUpdateNode
    ) {
      onUpdateNode({ label: zapTitle }, { shouldSave: true });
      /* Mark hasSetZapTitleAsStepTitle as true to prevent this logic from running again.
       * We only want this to happen on page load, not when user empties the input manually */
      hasSetZapTitleAsStepTitle.current = true;
    }
  }, [label, zapTitle, onUpdateNode]);

  const stepSummaryItems = getItemsForStepSummary({
    actors,
    hasNote,
  });

  return (
    <>
      <div
        className={classNames(styles.wrapper, className, {
          [styles.draftNode]: shouldShowDraftState,
        })}
      >
        {shouldShowDraftState && <DraftTag assetType="Zap" />}
        {children}
        <StepStatusRow nodeId={id} assetType="zap" zapId={zapId} />
        <StepTitle
          nodeId={id}
          label={label}
          placeholder={!zapId ? "Choose or create a Zap" : undefined}
          isSelected={isSelected}
        />
        <FlexRow>
          <ZapMetrics zapId={zapId} isPublished={zap?.is_published} />
          <TimeSaved zapId={zapId} />
        </FlexRow>
        <AssetHeading
          nodeId={id}
          assetType="zap"
          isZapExpanded={isExpanded}
          onToggleZapSteps={toggleButton}
          zapSteps={steps}
        />
        {stepSummaryItems.length > 0 && (
          <StepSummary items={stepSummaryItems} isAssetNodeType />
        )}
      </div>
    </>
  );
}
